// Header bar for the customer portal.
//
// Source-of-truth: /var/www/kitchenos/customer-standalone.html:542-566 +
// customer-live.js:40-52, :152-157. Layout:
//   left:   retailer logo (or initials) + name stack ("<retailer>" / "Customer Portal")
//   centre: greeting ("Hi <FirstName>")
//   right:  notification bell + help icon + user chip (avatar + name)
//
// All icons are 24×24 inline SVG with currentColor stroke (per
// [[feedback_design_icons]] — no Font Awesome / Material / emoji).

import { api } from "@tkos/api-client";

import type { CustomerIdentity, RetailerBranding, ToastKind } from "../types";

type ShowToast = (msg: string, kind?: ToastKind) => void;

interface CustomerNotif {
  id: string;
  type: string;
  text: string;
  /** Portal section to deep-link to when clicked (e.g. "quote", "messages"). */
  section: string | null;
  createdAt: string;
  readAt: string | null;
}

function escAttr(s: string): string {
  return String(s ?? "")
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}

/** Compact relative time ("just now", "3h", "2d") for the notification list. */
function timeAgo(iso: string): string {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return "";
  const s = Math.max(0, Math.round((Date.now() - t) / 1000));
  if (s < 60) return "just now";
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.round(h / 24);
  return `${d}d`;
}

/** Build the header element. Stateless; throw the result onto the page. */
export function buildHeader(
  branding: RetailerBranding,
  customer: CustomerIdentity,
  showToast: ShowToast,
): HTMLElement {
  const header = document.createElement("header");
  header.className = "cust-header";
  header.setAttribute("role", "banner");

  const inner = document.createElement("div");
  inner.className = "cust-header-inner";

  // ── Left: brand block (logo or initials, business name, portal tag) ──
  const left = document.createElement("div");
  left.className = "cust-header-left";

  const brand = document.createElement("div");
  brand.className = "cust-header-brand";

  const logo = document.createElement("div");
  logo.className = "cust-header-logo";
  if (branding.logoUrl) {
    const img = document.createElement("img");
    img.src = branding.logoUrl;
    img.alt = branding.businessName;
    img.addEventListener("error", () => {
      img.remove();
      logo.textContent = initialsOf(branding.businessName);
    });
    logo.appendChild(img);
  } else {
    logo.textContent = initialsOf(branding.businessName);
  }

  const nameStack = document.createElement("div");
  nameStack.className = "cust-header-name-stack";
  const name = document.createElement("div");
  name.className = "cust-header-name";
  name.textContent = branding.businessName || "Your Kitchen";
  const tag = document.createElement("div");
  tag.className = "cust-header-tag";
  tag.textContent = "Customer Portal";
  nameStack.append(name, tag);

  brand.append(logo, nameStack);
  left.appendChild(brand);

  // ── Centre: greeting ─────────────────────────────────────────────────
  const greeting = document.createElement("div");
  greeting.className = "cust-header-greeting";
  const firstName = (customer.firstName || "").trim();
  if (firstName) {
    greeting.appendChild(document.createTextNode("Hi "));
    const strong = document.createElement("strong");
    strong.textContent = firstName;
    greeting.appendChild(strong);
  } else {
    greeting.textContent = "Welcome";
  }

  // ── Right: bell + help + need-help link + user chip ──────────────────
  const right = document.createElement("div");
  right.className = "cust-header-right";

  // Need help (text + phone), shown on wider viewports only.
  // Uses ONLY the platform-allocated Twilio number — the retailer's
  // personal line is intentionally never exposed to customers
  // ([[feedback-contact-masking]]). The API omits the personal phone
  // entirely; this code couldn't display it even if asked.
  const helpline = branding.twilioNumber;
  if (helpline) {
    const help = document.createElement("a");
    help.className = "cust-header-helpline";
    help.href = `tel:${normalisePhone(helpline)}`;
    const lead = document.createElement("span");
    lead.className = "cust-header-helpline-lead";
    lead.textContent = "Need help?";
    const num = document.createElement("span");
    num.className = "cust-header-helpline-num";
    num.textContent = helpline;
    help.append(lead, num);
    right.appendChild(help);
  }

  // Notification bell with status dot + dropdown panel (Item 7 — was a
  // hardcoded "No new notifications" stub; now reads real customer-scoped
  // notifications and lets the customer jump straight to the relevant section).
  const bellWrap = document.createElement("div");
  bellWrap.className = "cust-bell-wrap";

  const bell = document.createElement("button");
  bell.type = "button";
  bell.className = "cust-header-iconbtn";
  bell.title = "Notifications";
  bell.setAttribute("aria-label", "Notifications");
  bell.innerHTML = `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/>
      <path d="M13.73 21a2 2 0 01-3.46 0"/>
    </svg>
    <span class="cust-header-iconbtn-dot" data-role="bell-dot" hidden></span>
  `;
  const dot = bell.querySelector<HTMLElement>("[data-role='bell-dot']")!;

  const panel = document.createElement("div");
  panel.className = "cust-bell-panel";
  panel.hidden = true;

  bellWrap.append(bell, panel);

  let notifs: CustomerNotif[] = [];
  let loaded = false;

  const updateDot = (): void => {
    dot.hidden = notifs.filter((n) => !n.readAt).length === 0;
  };

  const renderPanel = (): void => {
    if (notifs.length === 0) {
      panel.innerHTML = `<div class="cust-bell-empty">No notifications yet.</div>`;
      return;
    }
    panel.innerHTML =
      `<div class="cust-bell-head">Notifications</div>` +
      notifs
        .map(
          (n) => `
        <button type="button" class="cust-bell-item${n.readAt ? "" : " is-unread"}"${n.section ? ` data-nav="${escAttr(n.section)}"` : ""}>
          <span class="cust-bell-item-text">${escAttr(n.text)}</span>
          <span class="cust-bell-item-time">${escAttr(timeAgo(n.createdAt))}</span>
        </button>`,
        )
        .join("");
  };

  const load = async (): Promise<void> => {
    try {
      const resp = await api<{ ok: boolean; notifications?: CustomerNotif[] }>(
        "/customer/notifications",
        { method: "GET" },
      );
      notifs = Array.isArray(resp?.notifications) ? resp.notifications : [];
    } catch {
      notifs = [];
    }
    loaded = true;
    updateDot();
  };

  const markAllRead = async (): Promise<void> => {
    if (!notifs.some((n) => !n.readAt)) return;
    const now = new Date().toISOString();
    notifs = notifs.map((n) => ({ ...n, readAt: n.readAt ?? now }));
    updateDot();
    try {
      await api("/customer/notifications/read", { method: "POST", body: JSON.stringify({}) });
    } catch {
      /* best-effort — UI already reflects read */
    }
  };

  bell.addEventListener("click", (e) => {
    e.stopPropagation();
    const opening = panel.hidden;
    if (!opening) { panel.hidden = true; return; }
    const show = (): void => { renderPanel(); panel.hidden = false; void markAllRead(); };
    if (loaded) show();
    else void load().then(show);
  });

  panel.addEventListener("click", (e) => {
    const item = (e.target as HTMLElement | null)?.closest<HTMLElement>("[data-nav]");
    if (!item) return;
    const page = item.dataset["nav"];
    if (page) {
      window.dispatchEvent(new CustomEvent("cust-nav", { detail: { page } }));
      panel.hidden = true;
    }
  });

  document.addEventListener("click", (e) => {
    if (!bellWrap.contains(e.target as Node)) panel.hidden = true;
  });

  right.appendChild(bellWrap);

  // Prime the unread dot on load (without opening the panel).
  void load();

  // Help icon.
  const help = document.createElement("button");
  help.type = "button";
  help.className = "cust-header-iconbtn";
  help.title = "Help";
  help.setAttribute("aria-label", "Help");
  help.innerHTML = `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="10"/>
      <path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3"/>
      <line x1="12" y1="17" x2="12.01" y2="17"/>
    </svg>
  `;
  help.addEventListener("click", () => {
    showToast(
      "Need help? Message your retailer from the Messages tab — they'll get straight back to you.",
      "info",
    );
  });
  right.appendChild(help);

  // User chip.
  const userChip = document.createElement("div");
  userChip.className = "cust-header-user";
  const avatar = document.createElement("div");
  avatar.className = "cust-header-avatar";
  avatar.textContent = initialsOf(
    `${customer.firstName || ""} ${customer.lastName || ""}`.trim() ||
      customer.email ||
      "",
  );
  const who = document.createElement("div");
  who.className = "cust-header-who";
  const displayName = `${customer.firstName ?? ""}${customer.lastName ? ` ${customer.lastName}` : ""}`.trim();
  who.textContent = displayName || customer.email || "";
  userChip.append(avatar, who);
  right.appendChild(userChip);

  // ── Assemble ─────────────────────────────────────────────────────────
  inner.append(left, greeting, right);
  header.appendChild(inner);
  return header;
}

function initialsOf(s: string): string {
  const parts = (s || "").split(/[\s&]+/).filter(Boolean);
  const a = (parts[0]?.[0] ?? "?").toUpperCase();
  const b = (parts[1]?.[0] ?? "").toUpperCase();
  return (a + b) || "??";
}

/** Strip whitespace from a phone number so it works as a tel: target. */
function normalisePhone(p: string): string {
  return p.replace(/[^+\d]/g, "");
}
